import React from 'react';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white shadow-sm ring-1 ring-inset ring-white/10',
  secondary:
    'bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 active:bg-slate-100 text-slate-800 shadow-sm',
  danger:
    'bg-rose-700 hover:bg-rose-800 active:bg-rose-900 text-white shadow-sm',
  ghost:
    'bg-transparent hover:bg-brand-50 active:bg-brand-100 text-brand-700',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-9 px-3.5 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-[15px]',
};

const Button: React.FC<Props> = ({
  variant = 'primary',
  size = 'lg',
  loading = false,
  icon,
  fullWidth = false,
  disabled,
  className = '',
  children,
  type = 'button',
  ...rest
}) => {
  const classes = [
    'inline-flex items-center justify-center gap-2 rounded-lg font-semibold tracking-[-0.01em]',
    'transition-all duration-150 select-none whitespace-nowrap active:scale-[0.985]',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
    variantClasses[variant],
    sizeClasses[size],
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type={type} className={classes} disabled={disabled || loading} {...rest}>
      {loading && (
        <span
          data-testid="button-spinner"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      )}
      {!loading && icon && <span className="shrink-0" aria-hidden>{icon}</span>}
      {children}
    </button>
  );
};

export default Button;
